import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  OnDestroy
} from '@angular/core';
import { SharedModule } from '../../shared.module';
import { BannerStateService } from '../../../services/banner-state.service';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';
import { Role } from '../../../core/permissions/roles.enum';
import { PermissionsService } from '../../../core/permissions/permissions.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ImportProgressModalComponent } from '../import-progress-modal/import-progress-modal.component';
import { ImportProgressService } from '../../../services/import-progress.service';
import { ElixirImportProgressService } from '../../../services/elixir-import-progress.service';
import { ShopImportService } from '../../../services/shop-import.service';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { ShopState } from '../../../_state/shop/shop.state';
import { environment } from '../../../../environments/environment';
import { NotificationsWsService } from '../../../services/user-notifications/notifications-ws.service';
import { MerchantSubscriptionService } from '../../../services/subscription/merchant-subscription.service';
import { StripeService } from '../../../core/stripe/stripe.service';
import { CapModalService } from '../../../services/cap-modal.service';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { LocalStorageKeys } from '../../../core/constants/local-storage-keys.enum';
import { RegistrationStatusService } from '../../../core/services/registration-status.service';
import { TiktokTokenCheckerService } from '../../../core/tiktok-token-checker/tiktok-token-checker.service';
import { AuthShopService } from '../../../core/auth-shop/auth-shop.service';
import { AgencyStateService } from '../../../core/services/agency-state.service';
import { OffersService } from '../../../services/offers.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-banner',
  imports: [SharedModule],
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BannerComponent implements OnInit, OnDestroy {
  private bannerState = inject(BannerStateService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private permissionsService = inject(PermissionsService);
  private modalService = inject(NgbModal);
  private nodeImportService = inject(ImportProgressService);
  private elixirImportService = inject(ElixirImportProgressService);
  private shopImportService = inject(ShopImportService);
  private notificationsWs = inject(NotificationsWsService);
  private subscriptionService = inject(MerchantSubscriptionService);
  private stripeService = inject(StripeService);
  private capModalService = inject(CapModalService);
  private localStorage = inject(LocalStorageService);
  private registrationStatusService = inject(RegistrationStatusService);
  private tiktokTokenCheckerService = inject(TiktokTokenCheckerService);
  private authShopService = inject(AuthShopService);
  private agencyStateService = inject(AgencyStateService);
  private offersService = inject(OffersService);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);
  private importProgressService: ImportProgressService | ElixirImportProgressService;
  private store = inject(Store);
  private destroy$ = new Subject<void>();
  private subscribedShops = new Set<string>();
  private useElixir = true; // Elixir is the only import backend

  // Activity tracking for import timeout
  private lastActivityTime: number = Date.now();
  private lastActivityWriteTime = 0;
  private static readonly ACTIVITY_WRITE_THROTTLE_MS = 5_000;
  private inactivityCheckInterval?: any;
  private activeImportPollingInterval?: any;
  private warningShown = false;

  // Test mode configuration - use shorter timeouts for testing
  private readonly TEST_MODE =
    this.localStorage.get<string>(LocalStorageKeys.TEST_IMPORT_TIMEOUT) === 'true' ||
    window.location.search.includes('test-import-timeout=true');
  private readonly INACTIVITY_WARNING = this.TEST_MODE ? 30000 : 5 * 60 * 1000; // 30 sec vs 5 min
  private readonly INACTIVITY_TIMEOUT = this.TEST_MODE ? 60000 : 2 * 60 * 60 * 1000; // 1 min vs 2 hours (supports multi-hour imports)

  role = this.permissionsService.role;

  showCreditsBanner = this.bannerState.isBannerVisible;
  showDataImportBanner = this.bannerState.isDataImportInProgress;
  showSubscriptionBanner = this.bannerState.showSubscriptionBanner;
  showRegistrationBanner = this.bannerState.showRegistrationBanner;
  showAuthExpiredBanner = this.bannerState.showAuthExpiredBanner;
  /** Exposed for template — hides shop-scoped banners (e.g. TikTok auth-expired)
   *  when the user is on agency-level routes. The state signal is only cleared
   *  on shop SWITCH (see line ~1005), so without this guard the banner persists
   *  when the user navigates from a disconnected shop to /agency/*. (bd-g8pd) */
  isAgencyLevel = this.agencyStateService.isAgencyLevel;
  showReactivationBanner = this.bannerState.showReactivationBanner;
  reactivationOffer = this.bannerState.reactivationOffer;
  subscriptionBannerConfig = () => this.bannerState.getSubscriptionBannerConfig();
  registrationStatus$ = this.registrationStatusService.registrationStatus$;
  isDemoMode = false;
  isCreditChecking = false;
  isClaimingOffer = false;

  // Feature flag to control import progress modal visibility
  readonly enableImportProgressModal = environment.enableImportProgressModal;

  // Dynamic control for showing progress button (set via import.started event)
  showProgressButton = false;

  ngOnInit() {
    // CRITICAL FIX: Check localStorage for actual backend used (not just feature flag)
    // This handles case where Node.js proxies to Elixir based on runtime feature flag
    const storedImport = this.localStorage.get<string>(LocalStorageKeys.ACTIVE_IMPORT);
    let actualBackend = this.useElixir ? 'elixir' : 'nodejs';

    if (storedImport) {
      try {
        const importInfo = JSON.parse(storedImport);
        if (importInfo.backend) {
          actualBackend = importInfo.backend;
        }
      } catch (e) {
        // Invalid JSON, use default from feature flag
      }
    }

    // Select the appropriate import progress service based on actual backend
    this.importProgressService =
      actualBackend === 'elixir' ? this.elixirImportService : this.nodeImportService;

    // CRITICAL FIX: Update useElixir flag to match actual backend
    // This ensures checkActiveImportsFromBackend() uses correct backend
    this.useElixir = actualBackend === 'elixir';

    // Expose shop import service to window for testing
    this.shopImportService.exposeToWindow();
    this.bannerState.setBannerVisibility(false);

    this.initializeSubscriptionListeners();
    this.initializeRegistrationBanner();
    this.initializeAuthExpiredListener();
    this.checkPendingOffers();
    this.checkRouteForDemoMode(this.router.url);

    // Subscribe to router events to detect URL changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.checkRouteForDemoMode(event.urlAfterRedirects);
      });

    // Check for stored import state on page load
    this.checkStoredImportState();

    // Check for stored showProgressModal flag
    const storedShowProgress = this.localStorage.get<string>(
      LocalStorageKeys.IMPORT_SHOW_PROGRESS_MODAL
    );
    if (storedShowProgress === 'true') {
      this.showProgressButton = true;
      this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
    }

    // Check for active imports from backend (for imports started via Postman or API)
    // This should run regardless of active shop, as imports might be for other shops
    // Check both Elixir and Node.js backends
    if (this.useElixir) {
      // Use setTimeout to ensure WebSocket is initialized first
      setTimeout(() => {
        this.checkActiveImportsFromBackend();
      }, 1000);
    }
    // For Node.js backend, check via WebSocket active imports
    // The getActiveImports() subscription will handle this

    // Re-subscribe to shops when WebSocket reconnects
    this.importProgressService
      .getConnectionStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(connected => {
        if (connected) {
          if (this.subscribedShops.size > 0) {
            for (const shopId of this.subscribedShops) {
              this.importProgressService.subscribeToShop(shopId);
            }
          }
        }
      });

    // Subscribe to WebSocket import status updates
    this.importProgressService
      .getActiveImports()
      .pipe(takeUntil(this.destroy$))
      .subscribe(activeImports => {
        if (activeImports) {
          // CRITICAL SECURITY FIX: Only show banner if current shop is importing
          // This prevents merchant A from seeing merchant B's import banner
          const activeShop = this.store.selectSnapshot(ShopState.activeShop);

          // Check 1: Must have active shop selected
          if (!activeShop) {
            this.bannerState.setDataImportInProgress(false);
            return;
          }

          // Check 2: Merchant must match
          if (activeShop.merchantId !== activeImports.merchantId) {
            this.bannerState.setDataImportInProgress(false);
            return;
          }

          // Check 3: Current shop must be in the importing shops list
          const isCurrentShopImporting =
            activeImports.activeShops && activeImports.activeShops.includes(activeShop.shopId);

          this.bannerState.setDataImportInProgress(
            activeImports.hasActiveImports && isCurrentShopImporting
          );

          // Track activity only if current shop is actually importing
          if (activeImports.hasActiveImports && isCurrentShopImporting) {
            this.updateActivity();
            this.startInactivityCheck();

            // Store active imports in localStorage for recovery after page refresh
            if (activeImports.activeShops && activeImports.activeShops.length > 0) {
              const merchantId = activeShop?.merchantId || activeImports.merchantId || '';

              this.localStorage.set(
                LocalStorageKeys.ACTIVE_IMPORT,
                JSON.stringify({
                  shopIds: activeImports.activeShops,
                  merchantId: merchantId,
                  startedAt: Date.now(),
                  lastActivity: Date.now(),
                  backend: this.useElixir ? 'elixir' : 'nodejs'
                })
              );
            }
          } else {
            this.stopInactivityCheck();

            // Clear localStorage when no active imports
            this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
          }

          // Subscribe to shop-specific rooms only for the current shop
          // This ensures we only receive updates for our own shop
          if (isCurrentShopImporting && activeShop) {
            if (!this.subscribedShops.has(activeShop.shopId)) {
              this.importProgressService.subscribeToShop(activeShop.shopId);
              this.subscribedShops.add(activeShop.shopId);
            }
          } else {
            // Unsubscribe from all shops when current shop is not importing
            for (const shopId of this.subscribedShops) {
              this.importProgressService.unsubscribeFromShop(shopId);
            }
            this.subscribedShops.clear();
          }
        }
      });

    // Subscribe to import status changes
    this.importProgressService
      .getImportStatusChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        // Update activity on any status change
        if (event.type === 'import.started') {
          // CRITICAL SECURITY: Verify merchant and shop before showing banner
          // This prevents merchant A from seeing merchant B's import banner
          const activeShop = this.store.selectSnapshot(ShopState.activeShop);
          const eventMerchantId = event.merchantId || '';
          const eventShopId = event.shopId || '';
          // Check 1: Must have active shop
          if (!activeShop) {
            return;
          }

          // Check 2: Merchant must match
          if (activeShop.merchantId !== eventMerchantId) {
            return;
          }

          // Check 3: Shop must match
          if (activeShop.shopId !== eventShopId) {
            return;
          }

          this.updateActivity();
          // Ensure banner is shown when import starts
          this.bannerState.setDataImportInProgress(true);
          this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
          // Subscribe to the shop if not already subscribed
          if (eventShopId && !this.subscribedShops.has(eventShopId)) {
            this.importProgressService.subscribeToShop(eventShopId);
            this.subscribedShops.add(eventShopId);

            // Store import info in localStorage for recovery after page refresh
            this.localStorage.set(
              LocalStorageKeys.ACTIVE_IMPORT,
              JSON.stringify({
                shopIds: Array.from(this.subscribedShops),
                merchantId: eventMerchantId,
                startedAt: Date.now(),
                lastActivity: Date.now(),
                backend: this.useElixir ? 'elixir' : 'nodejs'
              })
            );
          }

          // Handle showProgressModal flag from import.started event
          if ((event as any).showProgressModal === true) {
            this.showProgressButton = true;
            this.localStorage.set(LocalStorageKeys.IMPORT_SHOW_PROGRESS_MODAL, 'true');
            this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
          } else {
            // CRITICAL FIX: Clear flag when showProgressModal is false or not provided
            // This prevents stale localStorage from previous imports with showProgressModal: true
            this.showProgressButton = false;
            this.localStorage.remove(LocalStorageKeys.IMPORT_SHOW_PROGRESS_MODAL);
            this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
          }
        }

        // Handle import completion when running in background
        if (event.type === 'import.completed') {
          // CRITICAL FIX: Only reload page if completed import is for the CURRENT active shop
          // This prevents reloading page when other shops' imports complete
          const activeShop = this.store.selectSnapshot(ShopState.activeShop);
          const eventShopId = event.shopId || '';

          // Check if this completion event is for the current shop
          const isCurrentShopCompleted = activeShop && activeShop.shopId === eventShopId;

          // Clear the import banner only if current shop's import completed
          if (isCurrentShopCompleted) {
            this.bannerState.setDataImportInProgress(false);
            this.stopInactivityCheck();

            // Clear localStorage
            this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
            this.localStorage.remove(LocalStorageKeys.IMPORT_SHOW_PROGRESS_MODAL);
            this.showProgressButton = false;
            this.cdr.markForCheck(); // Trigger change detection for OnPush strategy

            // Refresh the page after a short delay to ensure data is updated
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
        }

        // Handle import failure
        if (event.type === 'import.failed') {
          // CRITICAL FIX: Only clear state if failed import is for the CURRENT active shop
          const activeShop = this.store.selectSnapshot(ShopState.activeShop);
          const eventShopId = event.shopId || '';

          // Check if this failure event is for the current shop
          const isCurrentShopFailed = activeShop && activeShop.shopId === eventShopId;

          if (isCurrentShopFailed) {
            this.stopInactivityCheck();

            // Clear localStorage on failure
            this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
            this.localStorage.remove(LocalStorageKeys.IMPORT_SHOW_PROGRESS_MODAL);
            this.showProgressButton = false;
            this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
          }
        }
      });

    // Also subscribe to detailed import progress events for completion
    this.importProgressService
      .getImportProgress()
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        // Update activity on any progress event
        if (event.type === 'import.progress' || event.type === 'step.progress') {
          this.updateActivity();
        }

        // Handle core completion - just log but don't reload
        if (event.type === 'import.core.completed') {
          // Update activity to reset timeout since import is still running
          this.updateActivity();
          // Do NOT reload page, do NOT clear banner, import is still active
        }

        // Handle full completion - reload page
        if (event.type === 'import.completed') {
          // CRITICAL FIX: Only reload page if completed import is for the CURRENT active shop
          // This prevents reloading page when other shops' imports complete
          const activeShop = this.store.selectSnapshot(ShopState.activeShop);
          const eventShopId = event.shopId || '';

          // Always unsubscribe from the completed shop
          if (event.shopId && this.subscribedShops.has(event.shopId)) {
            this.importProgressService.unsubscribeFromShop(event.shopId);
            this.subscribedShops.delete(event.shopId);
          }

          // Check if this completion event is for the current shop
          const isCurrentShopCompleted = activeShop && activeShop.shopId === eventShopId;

          // Only clear banner and reload if current shop's import completed
          if (isCurrentShopCompleted) {
            // Clear the import banner and stop activity checking
            this.bannerState.setDataImportInProgress(false);
            this.stopInactivityCheck();

            // Clear localStorage
            this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);

            // Refresh the page after a short delay to ensure data is updated
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
        }

        // Handle import failure
        if (event.type === 'import.failed') {
          // CRITICAL FIX: Only clear banner if failed import is for the CURRENT active shop
          const activeShop = this.store.selectSnapshot(ShopState.activeShop);
          const eventShopId = event.shopId || '';

          // Unsubscribe from the failed shop
          if (event.shopId && this.subscribedShops.has(event.shopId)) {
            this.importProgressService.unsubscribeFromShop(event.shopId);
            this.subscribedShops.delete(event.shopId);
          }

          // Check if this failure event is for the current shop
          const isCurrentShopFailed = activeShop && activeShop.shopId === eventShopId;

          // Only clear banner if current shop's import failed
          if (isCurrentShopFailed) {
            // Clear the import banner and stop activity checking
            this.bannerState.setDataImportInProgress(false);
            this.stopInactivityCheck();

            // Clear localStorage on failure
            this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
          }
        }
      });
  }

  ngOnDestroy() {
    // Clean up activity tracking
    this.stopInactivityCheck();

    // Unsubscribe from all shop rooms
    for (const shopId of this.subscribedShops) {
      this.importProgressService.unsubscribeFromShop(shopId);
    }
    this.subscribedShops.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkRouteForDemoMode(url: string) {
    this.isDemoMode = url.includes('/demo') && this.authService.isDemoMode();

    this.bannerState.setBannerVisibility(false);

    if (this.isDemoMode) {
      this.bannerState.setBannerVisibility(true);
    } else {
      if (this.role() === Role.SHOP_OWNER) {
        this.checkCreditsBalance();
      }
    }

    // Trigger change detection for OnPush strategy
    this.cdr.markForCheck();
  }

  private checkCreditsBalance() {
    if (!this.isCreditChecking) {
      this.isCreditChecking = true;
      this.bannerState.setBannerVisibility(false);
      this.isCreditChecking = false;
    }
  }

  isAnyBannerVisible(): boolean {
    // Shop-scoped banners (auth-expired) are hidden on agency-level routes — see bd-g8pd.
    const showAuthExpired = this.showAuthExpiredBanner() && !this.isAgencyLevel();
    return (
      this.isDemoMode ||
      (this.showCreditsBanner() && !this.isCreditChecking) ||
      this.showDataImportBanner() ||
      this.showSubscriptionBanner() ||
      this.showRegistrationBanner() ||
      showAuthExpired ||
      this.showReactivationBanner()
    );
  }

  getVisibleBannersCount(): number {
    let count = 0;
    if (this.isDemoMode) count++;
    if (this.showCreditsBanner() && !this.isCreditChecking) count++;
    if (this.showDataImportBanner()) count++;
    if (this.showSubscriptionBanner()) count++;
    if (this.showRegistrationBanner()) count++;
    if (this.showAuthExpiredBanner() && !this.isAgencyLevel()) count++;
    if (this.showReactivationBanner()) count++;
    return count;
  }

  showImportProgress() {
    // Check feature flag or dynamic flag before opening modal
    if (!this.enableImportProgressModal && !this.showProgressButton) {
      return;
    }

    const modalRef = this.modalService.open(ImportProgressModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    // Pass the current shop ID to the modal
    const activeShop = this.store.selectSnapshot(ShopState.activeShop);
    if (activeShop) {
      modalRef.componentInstance.shopId = activeShop.shopId || activeShop.id;
    }
  }

  exitDemoMode() {
    if (this.isDemoMode) {
      this.authService.demoLogout().subscribe(() => {
        this.localStorage.remove(LocalStorageKeys.COMPLETED_REGISTRATION_STEPS);
        // If user had an active session before demo, restore to dashboard
        if (this.authService.accessToken) {
          this.router.navigate(['/dashboard']);
        } else {
          this.router.navigate(['/login']);
        }
      });
    }
  }

  exitAndSignUp() {
    if (this.isDemoMode) {
      this.authService.demoLogout().subscribe(() => {
        this.router.navigate(['/sign-up']);
        this.localStorage.remove(LocalStorageKeys.COMPLETED_REGISTRATION_STEPS);
      });
    }
  }

  /**
   * Current demo type (`'brand'` / `'agency'`) read from `localStorage[DEMO_TYPE]`.
   * Used by the banner template to label the "Switch to ..." button. Falls back
   * to `'brand'` when the key is missing (legacy demo sessions started before
   * the agency demo landed).
   */
  get currentDemoType(): 'brand' | 'agency' {
    const stored = this.localStorage.get<string>(LocalStorageKeys.DEMO_TYPE);
    return stored === 'agency' ? 'agency' : 'brand';
  }

  /**
   * Switch between brand demo and agency demo without leaving demo mode.
   * Logs out the current demo session, then re-enters demo with the opposite
   * type. Agency switch hydrates `AgencyStateService` before navigating so
   * sidebar / header / guards see `isAgency = true` on first paint, matching
   * the `DemoLoginComponent` / `DemoGuard` chooser flow.
   */
  switchDemoType() {
    if (!this.isDemoMode) return;
    const nextType: 'brand' | 'agency' = this.currentDemoType === 'agency' ? 'brand' : 'agency';
    this.authService.demoLogout().subscribe(() => {
      this.authService.demoLogin(nextType).subscribe(response => {
        if (!response?.accessToken) return;
        const navigate = () => {
          const landing = nextType === 'agency' ? '/demo/agency/dashboard' : '/demo/dashboard';
          this.router.navigateByUrl(landing, { replaceUrl: true });
        };
        if (nextType === 'agency') {
          this.agencyStateService.fetchAndUpdate().then(navigate).catch(navigate);
        } else {
          navigate();
        }
      });
    });
  }

  /**
   * Update last activity time and store in localStorage
   */
  private updateActivity(): void {
    this.lastActivityTime = Date.now();
    this.warningShown = false;

    // Throttle localStorage writes to prevent browser freeze during rapid import events
    const now = this.lastActivityTime;
    if (now - this.lastActivityWriteTime < BannerComponent.ACTIVITY_WRITE_THROTTLE_MS) {
      return;
    }
    this.lastActivityWriteTime = now;

    // Store in localStorage to persist across page refreshes
    this.localStorage.set(LocalStorageKeys.IMPORT_LAST_ACTIVITY, now.toString());
    this.localStorage.set(
      LocalStorageKeys.IMPORT_ACTIVE_SHOPS,
      JSON.stringify(Array.from(this.subscribedShops))
    );

    // Also update lastActivity in ACTIVE_IMPORT for accurate recovery after refresh
    const storedActiveImport = this.localStorage.get<string>(LocalStorageKeys.ACTIVE_IMPORT);
    if (storedActiveImport) {
      try {
        const importInfo = JSON.parse(storedActiveImport);
        importInfo.lastActivity = now;
        this.localStorage.set(LocalStorageKeys.ACTIVE_IMPORT, JSON.stringify(importInfo));
      } catch (e) {
        // Invalid JSON, skip update
      }
    }
  }

  /**
   * Start monitoring for inactivity
   */
  private startInactivityCheck(): void {
    // Don't start multiple intervals
    if (this.inactivityCheckInterval) {
      return;
    }

    const checkInterval = this.TEST_MODE ? 5000 : 30000; // Check every 5s in test mode, 30s normally

    // Check periodically for inactivity
    this.inactivityCheckInterval = setInterval(() => {
      this.checkInactivity();
    }, checkInterval);

    // Poll backend every 2 minutes to confirm import is still active
    // This prevents banner disappearing when SimpleBatchImporter sends no progress events
    if (!this.activeImportPollingInterval) {
      const pollingInterval = this.TEST_MODE ? 15000 : 2 * 60 * 1000; // 15s in test mode, 2 min normally
      this.activeImportPollingInterval = setInterval(() => {
        this.checkActiveImportsFromBackend();
      }, pollingInterval);
    }
  }

  /**
   * Stop monitoring for inactivity
   */
  private stopInactivityCheck(): void {
    if (this.inactivityCheckInterval) {
      clearInterval(this.inactivityCheckInterval);
      this.inactivityCheckInterval = undefined;
    }

    if (this.activeImportPollingInterval) {
      clearInterval(this.activeImportPollingInterval);
      this.activeImportPollingInterval = undefined;
    }

    // Clear stored state
    this.localStorage.remove(LocalStorageKeys.IMPORT_LAST_ACTIVITY);
    this.localStorage.remove(LocalStorageKeys.IMPORT_ACTIVE_SHOPS);
    this.localStorage.remove(LocalStorageKeys.IMPORT_WARNING_SHOWN);
  }

  /**
   * Check if import state was stored (for page refresh recovery)
   */
  private checkStoredImportState(): void {
    // First check for ACTIVE_IMPORT (newer format with multiple shops)
    const storedActiveImport = this.localStorage.get<string>(LocalStorageKeys.ACTIVE_IMPORT);
    if (storedActiveImport) {
      try {
        const importInfo = JSON.parse(storedActiveImport);
        const lastKnownActivity = importInfo.lastActivity || importInfo.startedAt || 0;
        const timeSinceLastActivity = Date.now() - lastKnownActivity;

        // If last activity was less than timeout ago, restore state
        if (timeSinceLastActivity < this.INACTIVITY_TIMEOUT) {
          // CRITICAL SECURITY FIX: Verify merchant and shop before restoring banner
          // This prevents showing import banner for wrong merchant/shop after localStorage recovery
          const activeShop = this.store.selectSnapshot(ShopState.activeShop);
          const storedMerchantId = importInfo.merchantId || '';
          const shopIds = importInfo.shopIds || (importInfo.shopId ? [importInfo.shopId] : []);

          // Check 1: Must have active shop
          if (!activeShop) {
            this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
            return;
          }

          // Check 2: Merchant must match
          if (activeShop.merchantId !== storedMerchantId) {
            this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
            return;
          }

          // Check 3: At least one shop ID must match active shop
          if (shopIds.length > 0 && !shopIds.includes(activeShop.shopId)) {
            this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
            return;
          }

          if (shopIds.length > 0) {
            // Restore shops
            for (const shopId of shopIds) {
              this.subscribedShops.add(shopId);
            }

            // Show banner and start tracking
            this.bannerState.setDataImportInProgress(true);
            this.lastActivityTime = lastKnownActivity || Date.now();
            this.startInactivityCheck();

            // Trigger change detection
            this.cdr.markForCheck();

            // Will re-subscribe when WebSocket connects (handled in connection status subscription)
            return;
          }
        } else {
          // Clear expired import info
          this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
        }
      } catch (e) {
        // Invalid data, clear it
        this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
      }
    }

    // Fallback to old format (IMPORT_LAST_ACTIVITY + IMPORT_ACTIVE_SHOPS)
    const storedActivity = this.localStorage.get<string>(LocalStorageKeys.IMPORT_LAST_ACTIVITY);
    const storedShops = this.localStorage.get<string>(LocalStorageKeys.IMPORT_ACTIVE_SHOPS);
    const warningShown = this.localStorage.get<string>(LocalStorageKeys.IMPORT_WARNING_SHOWN);

    if (storedActivity && storedShops) {
      const lastActivity = parseInt(storedActivity, 10);
      const timeSinceActivity = Date.now() - lastActivity;

      // If it's been more than timeout, clear everything
      if (timeSinceActivity > this.INACTIVITY_TIMEOUT) {
        this.stopInactivityCheck();
        this.bannerState.setDataImportInProgress(false);
        return;
      }

      // CRITICAL SECURITY FIX: Verify shop before restoring banner (old format)
      // Old format doesn't store merchantId, so we can only verify shop ID
      // This is less secure than new format, but better than nothing
      const activeShop = this.store.selectSnapshot(ShopState.activeShop);

      // Check 1: Must have active shop
      if (!activeShop) {
        this.localStorage.remove(LocalStorageKeys.IMPORT_LAST_ACTIVITY);
        this.localStorage.remove(LocalStorageKeys.IMPORT_ACTIVE_SHOPS);
        this.localStorage.remove(LocalStorageKeys.IMPORT_WARNING_SHOWN);
        return;
      }

      // Restore state
      this.lastActivityTime = lastActivity;
      this.warningShown = warningShown === 'true';

      try {
        const shops = JSON.parse(storedShops) as string[];

        // Check 2: At least one shop ID must match active shop
        if (shops && shops.length > 0 && !shops.includes(activeShop.shopId)) {
          this.localStorage.remove(LocalStorageKeys.IMPORT_LAST_ACTIVITY);
          this.localStorage.remove(LocalStorageKeys.IMPORT_ACTIVE_SHOPS);
          this.localStorage.remove(LocalStorageKeys.IMPORT_WARNING_SHOWN);
          return;
        }

        if (shops && shops.length > 0) {
          // Re-subscribe to shops
          for (const shopId of shops) {
            this.subscribedShops.add(shopId);
            // Will re-subscribe when WebSocket connects
          }

          // Resume activity checking
          this.bannerState.setDataImportInProgress(true);
          this.startInactivityCheck();
        }
      } catch (e) {
        this.stopInactivityCheck();
        // Clear invalid data
        this.localStorage.remove(LocalStorageKeys.IMPORT_LAST_ACTIVITY);
        this.localStorage.remove(LocalStorageKeys.IMPORT_ACTIVE_SHOPS);
        this.localStorage.remove(LocalStorageKeys.IMPORT_WARNING_SHOWN);
      }
    }
  }

  /**
   * Check for inactivity and handle timeout
   */
  private checkInactivity(): void {
    const timeSinceActivity = Date.now() - this.lastActivityTime;
    const secondsSinceActivity = Math.floor(timeSinceActivity / 1000);

    // If we've exceeded the timeout, force clear the import state
    if (timeSinceActivity > this.INACTIVITY_TIMEOUT) {
      // Clear all import state
      this.bannerState.setDataImportInProgress(false);
      this.stopInactivityCheck();

      // Unsubscribe from all shops
      for (const shopId of this.subscribedShops) {
        this.importProgressService.unsubscribeFromShop(shopId);
      }
      this.subscribedShops.clear();

      // Show a notification if possible
      this.showTimeoutNotification();
    } else if (timeSinceActivity > this.INACTIVITY_WARNING && !this.warningShown) {
      this.warningShown = true;
      this.localStorage.set(LocalStorageKeys.IMPORT_WARNING_SHOWN, 'true');
    }
  }

  /**
   * Show timeout notification to user
   */
  private showTimeoutNotification(): void {
    // You can implement a toast notification here if you have a notification service
  }

  /**
   * Check for active imports from backend
   * This is used to detect imports started via Postman or other external sources
   * Checks ALL active imports for the merchant, not just the active shop
   */
  private checkActiveImportsFromBackend(): void {
    const elixirApiUrl = (environment as any).elixirApiUrl;
    if (!elixirApiUrl) {
      return;
    }

    // Use the endpoint that returns all active imports for the merchant
    const checkUrl = `${elixirApiUrl}/api/imports/active`;

    fetch(checkUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${this.localStorage.get<string>(LocalStorageKeys.ACCESS_TOKEN)}`,
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
          // Process all active imports
          const activeShops = new Set<string>();
          let merchantId: string | null = null;

          for (const importData of data.data) {
            if (importData.shop_id) {
              activeShops.add(importData.shop_id);
              if (!merchantId && importData.merchant_id) {
                merchantId = importData.merchant_id;
              }
            }
          }

          if (activeShops.size > 0 && merchantId) {
            // CRITICAL FIX: Only show banner if import belongs to active shop's merchant AND current shop
            // This prevents banner from showing for admin/spy users when viewing different merchant's shop
            // AND prevents showing banner for all shops when only one shop is importing
            const activeShop = this.store.selectSnapshot(ShopState.activeShop);
            const allShops = this.store.selectSnapshot(ShopState.shops);

            if (!activeShop || activeShop.merchantId !== merchantId) {
              // Import is for different merchant, don't show banner here
              // But still subscribe for updates in case user switches to that merchant
              return;
            }

            // CRITICAL FIX: Only show banner if the CURRENT ACTIVE SHOP is importing
            // Not just if ANY shop in the merchant is importing
            if (!activeShops.has(activeShop.shopId)) {
              // Current shop is not importing, don't show banner
              // Even though another shop in the same merchant might be importing
              return;
            }

            // Store in localStorage
            this.localStorage.set(
              LocalStorageKeys.ACTIVE_IMPORT,
              JSON.stringify({
                shopIds: Array.from(activeShops),
                merchantId: merchantId,
                startedAt: Date.now(),
                lastActivity: Date.now(),
                backend: 'elixir'
              })
            );

            // Show import banner
            this.bannerState.setDataImportInProgress(true);

            // Subscribe to all active shops for real-time updates
            if (this.importProgressService) {
              for (const shopId of activeShops) {
                if (!this.subscribedShops.has(shopId)) {
                  this.importProgressService.subscribeToShop(shopId);
                  this.subscribedShops.add(shopId);
                }
              }
            }

            // Start activity tracking
            this.updateActivity();
            this.startInactivityCheck();

            // Trigger change detection
            this.cdr.markForCheck();
          }
        } else {
          // CRITICAL FIX: Hide banner if no active imports found
          // This handles the case when clean-shop-data.sh deletes flow:*:active keys
          this.bannerState.setDataImportInProgress(false);
          this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
          this.cdr.markForCheck();
        }
      })
      .catch(() => {
        // Silently ignore - Elixir import service may not be running (e.g., local dev)
      });
  }

  private checkPendingOffers(): void {
    this.offersService.getPendingOffer().subscribe({
      next: offer => {
        // Always sync the result — including null — so an offer that has since
        // expired / been claimed / been dismissed elsewhere in the SPA session
        // is cleared, rather than leaving a phantom 50% preview on the
        // subscriptions picker. setReactivationOffer(null) clears the state.
        this.bannerState.setReactivationOffer(offer);
        this.cdr.markForCheck();
      },
      error: () => {
        // Silently fail — don't block app load
      }
    });
  }

  private initializeSubscriptionListeners(): void {
    this.notificationsWs.subscriptionStopped$.pipe(takeUntil(this.destroy$)).subscribe(status => {
      this.bannerState.setSubscriptionStatus(status);
    });

    this.notificationsWs.subscriptionActivated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.bannerState.setSubscriptionStatus(null);
      this.registrationStatusService.markAsComplete();
    });

    this.subscriptionService.getSubscriptionStatus().pipe(takeUntil(this.destroy$)).subscribe();
  }

  private initializeRegistrationBanner(): void {
    // Check registration status on init
    this.registrationStatusService.checkStatus();

    // Subscribe to registration status changes
    this.registrationStatus$.pipe(takeUntil(this.destroy$)).subscribe(status => {
      // Show banner only if registration is incomplete, user is not in demo mode,
      // and user is NOT an agency user (agency users complete registration differently
      // and don't need to connect a TikTok shop).
      const shouldShow =
        status.isIncomplete && !this.isDemoMode && !this.agencyStateService.isAgency();
      this.bannerState.setRegistrationBannerVisibility(shouldShow);
      this.cdr.markForCheck();
    });
  }

  private initializeAuthExpiredListener(): void {
    this.tiktokTokenCheckerService.tikTokTokenState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isValid: boolean) => {
        if (!isValid) {
          this.bannerState.setAuthExpired(true);
        } else {
          this.bannerState.setAuthExpired(false);
        }
        this.cdr.markForCheck();
      });

    // Reset auth expired banner when switching shops.
    // The backend only sends X-TikTok-Token-Status header when token is expired,
    // so nothing clears the banner when navigating to a shop with a valid token.
    // If the new shop also has an expired token, the next API response will re-trigger it.
    this.store
      .select(ShopState.activeShop)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.bannerState.setAuthExpired(false);
        this.cdr.markForCheck();
      });
  }

  handleCtaAction(ctaText: string): void {
    const actions: Record<string, () => void> = {
      'Update payment method': () =>
        this.stripeService.createCustomerPortalSession().subscribe({
          next: response => {
            window.location.href = response.url;
          },
          error: error => {
            console.error('Error creating checkout session:', error);
          }
        }),
      'Upgrade plan': () =>
        this.router.navigate(['/settings'], { queryParams: { section: 'subscriptions' } }),
      'Upgrade Plan': () =>
        this.router.navigate(['/settings'], { queryParams: { section: 'subscriptions' } }),
      'Increase CAP': () => this.capModalService.openCapModal(),
      'Select Plan': () =>
        this.router.navigate(['/settings'], { queryParams: { section: 'subscriptions' } })
    };

    actions[ctaText]?.();
  }

  goToSubscriptionSettings(): void {
    this.router.navigate(['/settings'], { queryParams: { section: 'subscriptions' } });
  }

  claimReactivationOffer(): void {
    const offer = this.bannerState.reactivationOffer();
    if (!offer || this.isClaimingOffer) return;

    this.isClaimingOffer = true;
    this.cdr.markForCheck();

    this.offersService.claimOffer(offer.offerType).subscribe({
      next: response => {
        if (response.checkoutUrl) {
          window.location.href = response.checkoutUrl;
        } else {
          this.toastr.success(response.message || 'Subscription reactivated!');
          this.bannerState.clearReactivationOffer();
          this.isClaimingOffer = false;
          this.cdr.markForCheck();
        }
      },
      error: err => {
        this.toastr.error(err.error?.message || 'Failed to claim offer');
        this.isClaimingOffer = false;
        this.cdr.markForCheck();
      }
    });
  }

  reconnectShop(): void {
    const activeShop = this.store.selectSnapshot(ShopState.activeShop);
    if (activeShop?.id) {
      this.authShopService.reconnectTikTokShopWhenTokenExpired(activeShop.id).subscribe({
        next: response => {
          if (response?.redirectUrl) {
            window.location.href = response.redirectUrl;
          }
        },
        error: err => {
          console.error('Error reconnecting shop:', err);
        }
      });
    }
  }
}
